import type { FirstRunHint } from "../application/first-run-hint";
import type { GameFlow, GameFlowSnapshot } from "../application/game-flow";
import type {
  RoundRuntime,
  RoundRuntimeSnapshot,
} from "../application/round-runtime";
import { getRoundProgressPresentation } from "../application/player-status";
import { gameplayConfig, getModeConfig } from "../config/gameplay-config";

export const firstRunHintPlacement = "bottom-edge";

export interface GameplayHudElements {
  readonly hint: HTMLElement;
  readonly hintCopy: HTMLElement;
  readonly joystickVisualRoot: HTMLElement;
  readonly roundProgress: HTMLElement;
  readonly roundProgressAnnouncement: HTMLElement;
  readonly roundProgressFill: HTMLElement;
}

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing gameplay HUD element: ${id}`);
  }
  return element;
};

export class GameplayHud {
  private announcedMilestone: number | null = null;
  private readonly unsubscribers: readonly (() => void)[];

  public constructor(
    private readonly flow: GameFlow,
    private readonly round: RoundRuntime,
    private readonly firstRunHint: FirstRunHint,
    private readonly elements: GameplayHudElements,
  ) {
    const visual = gameplayConfig.virtualJoystick.visual;
    elements.joystickVisualRoot.style.setProperty(
      "--joystick-connector-alpha",
      String(visual.connectorAlpha),
    );
    elements.joystickVisualRoot.style.setProperty(
      "--joystick-fill-alpha",
      String(visual.fillAlpha),
    );
    elements.joystickVisualRoot.style.setProperty(
      "--joystick-knob-alpha",
      String(visual.knobAlpha),
    );
    elements.joystickVisualRoot.style.setProperty(
      "--joystick-outline-alpha",
      String(visual.outlineAlpha),
    );
    elements.joystickVisualRoot.style.setProperty(
      "--joystick-ring-alpha",
      String(visual.ringAlpha),
    );
    elements.hint.dataset.placement = firstRunHintPlacement;
    this.unsubscribers = [
      flow.subscribe(() => {
        this.render();
      }),
      round.subscribe(() => {
        this.render();
      }),
      firstRunHint.subscribe(() => {
        this.render();
      }),
    ];
    this.render();
  }

  public destroy(): void {
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
  }

  private render(): void {
    const flowSnapshot = this.flow.getSnapshot();
    const roundSnapshot = this.round.getSnapshot();
    this.renderTimer(flowSnapshot, roundSnapshot);
    this.renderHint();
  }

  private renderHint(): void {
    const hint = this.firstRunHint.getSnapshot();
    this.elements.hint.hidden = !hint.visible;
    this.elements.hint.dataset.stage = hint.stage;
    this.elements.hintCopy.textContent =
      hint.stage === "growth"
        ? "Eat food to grow. Bigger snakes beat smaller snakes."
        : "Touch and drag to steer.";
  }

  private renderTimer(
    flowSnapshot: GameFlowSnapshot,
    roundSnapshot: RoundRuntimeSnapshot,
  ): void {
    const modeConfig =
      flowSnapshot.mode === null ? null : getModeConfig(flowSnapshot.mode);
    const presentation = getRoundProgressPresentation(
      roundSnapshot.timeRemainingMs,
      modeConfig?.roundDurationMs ?? null,
      modeConfig?.finalWarningMs ?? null,
    );
    const visible = flowSnapshot.scene === "gameplay" && presentation.visible;
    this.elements.roundProgress.hidden = !visible;
    if (!visible) {
      this.announcedMilestone = null;
      this.elements.roundProgressAnnouncement.textContent = "";
      return;
    }
    this.elements.roundProgress.setAttribute(
      "data-round-section",
      presentation.finalSection ? "final" : "steady",
    );
    this.elements.roundProgressFill.style.setProperty(
      "--round-progress-percent",
      `${String(presentation.percent)}%`,
    );
    const milestone = presentation.assistiveMilestone;
    if (milestone !== null && milestone.percent !== this.announcedMilestone) {
      this.announcedMilestone = milestone.percent;
      this.elements.roundProgress.setAttribute(
        "aria-valuenow",
        String(milestone.percent),
      );
      this.elements.roundProgress.setAttribute(
        "aria-valuetext",
        milestone.text,
      );
      this.elements.roundProgressAnnouncement.textContent = milestone.text;
    }
  }
}

export const installGameplayHud = (
  documentScope: Document,
  flow: GameFlow,
  round: RoundRuntime,
  firstRunHint: FirstRunHint,
): GameplayHud =>
  new GameplayHud(flow, round, firstRunHint, {
    hint: requireElement(documentScope, "first-run-hint"),
    hintCopy: requireElement(documentScope, "first-run-hint-copy"),
    joystickVisualRoot: documentScope.documentElement,
    roundProgress: requireElement(documentScope, "round-progress"),
    roundProgressAnnouncement: requireElement(
      documentScope,
      "round-progress-announcement",
    ),
    roundProgressFill: requireElement(documentScope, "round-progress-fill"),
  });
